import { Injectable, inject } from '@angular/core';
import {
  BackendService,
  BaseApiService,
  RequestFacadeModel,
  RequestModel,
  RequestType,
} from '@cf/core';
import { ITimeslots, SchemaBookMeeting, SchemaMeetingInfo } from '@cf/temp/services';
import { TranslateService } from '@tolgee/ngx';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class BookingApiService extends BaseApiService {
  translateService = inject(TranslateService);
  constructor(backendService: BackendService) {
    super(backendService, 'booking');
  }

  booking(path: string, username: string): Observable<SchemaMeetingInfo> {
    const request: RequestModel = new RequestModel<null>({
      url: path
        ? this.getFullUrl(`${username}/${path}`)
        : this.getFullUrl(username, 'booking-no-path'),
      redirectOnError: '/error',
      skipNotify: true,
    });
    const requestFacade: RequestFacadeModel = new RequestFacadeModel<null>({
      requestType: RequestType.get,
      request,
    });
    return this.send<SchemaMeetingInfo, null>(requestFacade);
  }

  timeslots(
    path: string,
    username: string,
    dto: {
      timezone: string;
      from_date?: string;
      to_date?: string;
      duration: string;
      rescheduling_event_id?: number;
    },
  ): Observable<ITimeslots[]> {
    const request: RequestModel = new RequestModel<null>({
      url: path
        ? this.getFullUrl(`${username}/${path}/timeslots`)
        : this.getFullUrl(`${username}/timeslots`, 'booking-no-path'),
      redirectOnError: '/error',
      skipNotify: true,
    }).withQuery(dto);
    const requestFacade: RequestFacadeModel = new RequestFacadeModel<null>({
      requestType: RequestType.get,
      request,
    });
    return this.send<ITimeslots[], null>(requestFacade);
  }

  hasFreeSlots(
    path: string,
    username: string,
    dto: {
      duration: string;
      rescheduling_event_id?: number;
    },
  ): Observable<boolean> {
    const request: RequestModel<{
      duration: string;
      rescheduling_event_id?: number;
    }> = new RequestModel({
      url: path
        ? this.getFullUrl(`${username}/${path}/has-free-timeslot`)
        : this.getFullUrl(`${username}/has-free-timeslot`, 'booking-no-path'),
      skipNotify: true,
      requestBody: dto,
    });
    const requestFacade: RequestFacadeModel<{
      duration: string;
      rescheduling_event_id?: number;
    }> = new RequestFacadeModel({
      requestType: RequestType.post,
      request,
    });
    return this.send<any, any>(requestFacade).pipe(map((res) => res.has_free_time_slots));
  }

  book(path: string, username: string, dto: any): Observable<SchemaBookMeeting> {
    const request: RequestModel = new RequestModel<null>({
      url: path
        ? this.getFullUrl(`${username}/${path}/book`)
        : this.getFullUrl(`${username}/book`, 'booking-no-path'),
      requestBody: dto,
    });
    const requestFacade: RequestFacadeModel = new RequestFacadeModel<null>({
      requestType: RequestType.post,
      request,
    });
    return this.send<SchemaBookMeeting, null>(requestFacade);
  }

  checkPassword(path: string, username: string, dto: any): Observable<any> {
    const request: RequestModel = new RequestModel<null>({
      url: path
        ? this.getFullUrl(`${username}/${path}/check-password`)
        : this.getFullUrl(`${username}/check-password`, 'booking-no-path'),
      requestBody: dto,
      skipNotify: true,
      successMessage: { message: this.translateService.instant('booking.correct_password') },
    });
    const requestFacade: RequestFacadeModel = new RequestFacadeModel<null>({
      requestType: RequestType.post,
      request,
    });
    return this.send<any, null>(requestFacade);
  }
}
